// Tasks → Automations tab: the org's simple rules from three fixed kinds
// (notify on done, notify on overdue, assign tasks added to a project).
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { Button } from '../../components/ui/button'
import {
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import { EmptyState } from '../../components/ui/empty-state'
import { Field } from '../../components/ui/field'
import { Select } from '../../components/ui/select'
import { formatDateTime } from '../../lib/datetime'
import { toast } from '../../lib/toast'
import { cn } from '../../lib/utils'
import { useT } from '../../i18n'
import { staffTasksI18n } from '../../i18n/shared/staff-tasks'
import {
  createAutomation,
  deleteAutomation,
  getProject,
  listAssignees,
  listAutomations,
  listProjects,
  setAutomationEnabled,
  StaffTaskApiError,
  staffTaskKeys,
  type Automation,
  type AutomationKind,
  type AutomationTarget,
} from './api'

const SURFACE =
  'rounded-[var(--surface-lifted-radius)] border border-[var(--surface-lifted-border)] bg-[var(--surface-lifted-bg)] p-4 shadow-[var(--surface-lifted-shadow)]'
const KINDS: AutomationKind[] = ['done_notify', 'overdue_notify', 'project_assign']

export function AutomationsTab({
  createOpen,
  onCreateOpenChange,
}: {
  createOpen: boolean
  onCreateOpenChange: (open: boolean) => void
}) {
  const t = useT(staffTasksI18n)
  const a = t.automations
  const queryClient = useQueryClient()
  const rules = useQuery({ queryKey: staffTaskKeys.automations, queryFn: listAutomations })
  const refresh = () => void queryClient.invalidateQueries({ queryKey: staffTaskKeys.automations })
  const onError = () => toast.error(t.toasts.error)
  const toggle = useMutation({
    mutationFn: (rule: Automation) => setAutomationEnabled(rule.id, !rule.enabled),
    onSuccess: refresh,
    onError,
  })
  const remove = useMutation({
    mutationFn: (rule: Automation) => deleteAutomation(rule.id),
    onSuccess: () => {
      toast.success(a.toasts.deleted)
      refresh()
    },
    onError,
  })
  const items = rules.data?.items ?? []

  if (rules.isError) {
    return (
      <p role="alert" className="text-sm text-[var(--color-danger)]">
        {t.loadError}
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 text-sm text-[var(--color-text-muted)]">{a.description}</p>
      {!rules.isLoading && items.length === 0 ? (
        <EmptyState
          icon="taskComplete"
          title={a.empty}
          action={<Button onClick={() => onCreateOpenChange(true)}>{a.add}</Button>}
        />
      ) : (
        <ul className={cn(SURFACE, 'm-0 flex list-none flex-col p-0')} aria-busy={rules.isLoading}>
          {items.map((rule) => (
            <li
              key={rule.id}
              data-testid={`staff-automation-${rule.id}`}
              className="flex flex-wrap items-center gap-3 border-b border-[var(--color-border)] px-4 py-3 last:border-b-0"
            >
              <button
                type="button"
                role="switch"
                aria-checked={rule.enabled}
                aria-label={a.toggle(rule.enabled)}
                onClick={() => toggle.mutate(rule)}
                className={cn(
                  'relative h-5 w-9 shrink-0 rounded-full transition-colors',
                  rule.enabled ? 'bg-[var(--color-success)]' : 'bg-[var(--color-border-strong)]',
                )}
              >
                <span
                  className={cn(
                    'absolute top-0.5 h-4 w-4 rounded-full bg-[var(--color-surface)] shadow transition-[left]',
                    rule.enabled ? 'left-[1.125rem]' : 'left-0.5',
                  )}
                />
              </button>
              <div className="min-w-0 flex-1">
                <div
                  className={cn(
                    'text-sm',
                    rule.enabled ? 'text-[var(--color-text)]' : 'text-[var(--color-text-muted)]',
                  )}
                >
                  {describeRule(rule, t)}
                </div>
                <div className="text-xs text-[var(--color-text-muted)]">
                  {rule.run_count > 0
                    ? a.ran(rule.run_count, rule.last_run_at ? formatDateTime(rule.last_run_at) : null)
                    : a.neverRan}
                </div>
              </div>
              <button
                type="button"
                aria-label={a.remove}
                onClick={() => remove.mutate(rule)}
                className="inline-flex h-7 w-7 items-center justify-center rounded text-[var(--color-text-muted)] hover:text-[var(--color-danger)]"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
      <RuleDialog open={createOpen} onOpenChange={onCreateOpenChange} />
    </div>
  )
}

type Strings = (typeof staffTasksI18n)['en']

function describeRule(rule: Automation, t: Strings): string {
  const a = t.automations
  const scope = rule.project ? a.inProject(rule.project.name, rule.stage_name) : a.anyProject
  const who =
    rule.target === 'user' ? (rule.target_user?.display_name ?? '—') : a.targets[rule.target]
  return a.sentence[rule.kind](scope, who)
}

function RuleDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && <RuleForm onClose={() => onOpenChange(false)} />}
    </Dialog>
  )
}

function RuleForm({ onClose }: { onClose: () => void }) {
  const t = useT(staffTasksI18n)
  const a = t.automations
  const queryClient = useQueryClient()
  const [kind, setKind] = useState<AutomationKind>('done_notify')
  const [projectId, setProjectId] = useState('')
  const [stageId, setStageId] = useState('')
  const [target, setTarget] = useState<AutomationTarget>('opener')
  const [userId, setUserId] = useState('')
  const [error, setError] = useState<string | null>(null)

  const people = useQuery({ queryKey: staffTaskKeys.assignees, queryFn: listAssignees })
  const projects = useQuery({ queryKey: staffTaskKeys.projects, queryFn: listProjects })
  const project = useQuery({
    queryKey: staffTaskKeys.project(projectId),
    queryFn: () => getProject(projectId),
    enabled: !!projectId,
  })
  const assigning = kind === 'project_assign'
  const effectiveTarget: AutomationTarget = assigning ? 'user' : target

  const save = useMutation({
    mutationFn: () =>
      createAutomation({
        kind,
        project_id: projectId || null,
        stage_id: (projectId && stageId) || null,
        target: effectiveTarget,
        target_user_id: effectiveTarget === 'user' ? userId || null : null,
      }),
    onSuccess: () => {
      toast.success(a.toasts.created)
      void queryClient.invalidateQueries({ queryKey: staffTaskKeys.automations })
      onClose()
    },
    onError: (err) => {
      const code = err instanceof StaffTaskApiError ? err.code : undefined
      toast.error(code === 'assignee_not_in_team' ? t.toasts.notInTeam : t.toasts.error)
    },
  })

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (assigning && !projectId) return setError(a.form.projectRequired)
    if (effectiveTarget === 'user' && !userId) return setError(a.form.personRequired)
    save.mutate()
  }

  const stages = projectId ? (project.data?.stages ?? []) : []

  return (
    <DialogContent>
      <form onSubmit={submit} className="flex flex-col gap-4" data-testid="staff-automation-form">
        <DialogHeader>
          <DialogTitle>{a.form.title}</DialogTitle>
        </DialogHeader>
        <Field id="staff-automation-kind" label={a.form.kindLabel}>
          <Select
            value={kind}
            onChange={(e) => {
              setKind(e.target.value as AutomationKind)
              setError(null)
            }}
          >
            {KINDS.map((value) => (
              <option key={value} value={value}>
                {a.kinds[value]}
              </option>
            ))}
          </Select>
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="staff-automation-project" label={a.form.projectLabel}>
            <Select
              value={projectId}
              onChange={(e) => {
                setProjectId(e.target.value)
                setStageId('')
                setError(null)
              }}
            >
              <option value="">{assigning ? '—' : a.form.anyProject}</option>
              {(projects.data?.items ?? []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </Select>
          </Field>
          {stages.length > 0 && (
            <Field id="staff-automation-stage" label={a.form.stageLabel}>
              <Select value={stageId} onChange={(e) => setStageId(e.target.value)}>
                <option value="">{a.form.anyStage}</option>
                {stages.map((stage) => (
                  <option key={stage.id} value={stage.id}>
                    {stage.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {!assigning && (
            <Field id="staff-automation-target" label={a.form.targetLabel}>
              <Select
                value={target}
                onChange={(e) => setTarget(e.target.value as AutomationTarget)}
              >
                {(['opener', 'project_owner', 'user'] as const).map((value) => (
                  <option key={value} value={value}>
                    {a.targets[value]}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {effectiveTarget === 'user' && (
            <Field id="staff-automation-person" label={a.form.personLabel}>
              <Select
                value={userId}
                onChange={(e) => {
                  setUserId(e.target.value)
                  setError(null)
                }}
              >
                <option value="">—</option>
                {(people.data?.items ?? []).map((person) => (
                  <option key={person.user_id} value={person.user_id}>
                    {person.display_name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </div>
        {error && (
          <p role="alert" className="m-0 text-sm text-[var(--color-danger)]">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t.form.cancel}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {a.form.save}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}
